<template>
  <div class="page-content">
    <breadcrumb-index
      :items="breadcrumbs"
      icon="bell"
      :badge="totalCount ? `${totalCount} bulletins` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button @click="openBulkModal" class="btn btn-outline-primary btn-sm me-2" :disabled="!selectedExam" v-if="canModify">
          <vue-feather type="copy" size="14" class="me-1" />
          Bulk Create
        </button>
        <button @click="openCreateModal" class="btn btn-primary btn-sm" :disabled="!selectedExam" v-if="canModify">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Bulletin
        </button>
      </template>
    </breadcrumb-index>

    <!-- Exam Filter -->
    <div class="filter-cascade-section">
      <div class="filter-cascade-header">
        <vue-feather type="filter" size="18" />
        <h4>Select Exam to View Bulletins</h4>
      </div>
      <div class="filter-cascade-grid">
        <div class="filter-step" :class="{ active: true, completed: selectedExam }">
          <div class="step-number">1</div>
          <div class="step-content">
            <label class="step-label">Select Exam</label>
            <select v-model="selectedExam" class="filter-select" @change="onExamChange" :disabled="loadingExams">
              <option value="">{{ loadingExams ? 'Loading exams...' : 'Choose an exam...' }}</option>
              <option v-for="exam in exams" :key="exam.id" :value="exam.id">{{ exam.name }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulletins Table -->
    <div v-if="selectedExam" class="bulletins-content">
      <DataTable
        :items="filteredBulletins"
        :columns="columns"
        :loading="loading"
        :paginated="false"
        search-placeholder="Search bulletins..."
        empty-icon="bell"
        empty-title="No bulletins found"
        empty-description="Create your first bulletin for this exam."
      >
        <template #filters>
          <select v-model="filterType" class="filter-select-modern" @change="fetchBulletins">
            <option value="">All Types</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="success">Success</option>
            <option value="urgent">Urgent</option>
          </select>
          <select v-model="filterActive" class="filter-select-modern" @change="fetchBulletins">
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </template>

        <template #cell-title="{ item }">
          <div class="avatar-info">
            <span class="avatar-name">{{ item.title }}</span>
            <span v-if="item.url" class="avatar-subtitle">
              <vue-feather type="external-link" size="11" /> {{ item.url }}
            </span>
          </div>
        </template>

        <template #cell-type="{ item }">
          <span :class="['badge', getTypeBadge(item.type)]">{{ item.type }}</span>
        </template>

        <template #cell-status="{ item }">
          <span :class="['status-badge', item.is_active ? 'status-active' : 'status-inactive']">
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template #cell-published="{ item }">
          <span class="text-muted">{{ formatDate(item.published_at) }}</span>
        </template>

        <template #cell-expires="{ item }">
          <span v-if="item.expires_at" class="text-muted">{{ formatDate(item.expires_at) }}</span>
          <span v-else class="badge bg-light text-dark">No expiry</span>
        </template>

        <template #cell-actions="{ item }">
          <div class="action-buttons">
            <button v-if="canModify" class="action-btn" :class="item.is_active ? 'action-view' : ''" @click="toggleBulletin(item)"
              v-tooltip="item.is_active ? 'Deactivate' : 'Activate'"
              :style="!item.is_active ? 'color: #9ca3af;' : ''">
              <vue-feather :type="item.is_active ? 'toggle-right' : 'toggle-left'" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-edit" @click="openEditModal(item)" v-tooltip="'Edit bulletin'">
              <vue-feather type="edit-2" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-delete" @click="confirmDelete(item)" v-tooltip="{ text: 'Delete bulletin', variant: 'danger' }">
              <vue-feather type="trash-2" size="14" />
            </button>
          </div>
        </template>

        <template #empty-action>
          <button @click="openCreateModal" class="btn btn-primary">
            <vue-feather type="plus" size="16" class="me-1" />
            Add First Bulletin
          </button>
        </template>
      </DataTable>
    </div>

    <!-- Empty State -->
    <div v-else class="select-prompt">
      <div class="select-prompt-icon">
        <vue-feather type="bell" size="64" />
      </div>
      <h3>Select an Exam</h3>
      <p>Choose an exam from the filter above to view and manage its bulletins.</p>
    </div>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Bulletin' : 'Add Bulletin'"
      icon="bell"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.title || !form.message || !form.type"
      :item-id="isEditing ? editingId : ''"
      @submit="saveBulletin"
    >
      <form @submit.prevent="saveBulletin" class="modal-form">
        <div class="form-group">
          <label class="form-label">Title <span class="text-danger">*</span></label>
          <input v-model="form.title" type="text" class="form-control" placeholder="e.g., Exam Date Announced" maxlength="100" required />
          <small class="text-muted">{{ form.title.length }}/100 characters</small>
        </div>

        <!-- AI Enhance Button -->
        <div class="ai-enhance-section" v-if="form.title">
          <button type="button" @click="aiEnhanceBulletin" class="ai-enhance-btn" :disabled="aiEnhancing">
            <vue-feather :type="aiEnhancing ? 'loader' : 'cpu'" size="16" :class="{ 'spin': aiEnhancing }" />
            {{ aiEnhancing ? 'Generating...' : 'AI Generate Message' }}
          </button>
          <button type="button" @click="aiTranslateBulletin" class="ai-translate-btn" :disabled="aiTranslating">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'AI Translate' }}
          </button>
          <span class="ai-hint">Auto-generate bulletin message from title</span>
        </div>

        <!-- Translation Language Selector -->
        <div class="ai-translate-section" v-if="form.title && showTranslateOptions">
          <label class="form-label">Translate to</label>
          <select v-model="translateTargetLang" class="form-control form-control-sm" style="max-width: 200px;">
            <option v-for="lang in languageOptions.filter(l => l.code !== form.language)" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <button type="button" @click="confirmTranslateBulletin" class="ai-translate-btn" :disabled="aiTranslating || !translateTargetLang">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'Translate Now' }}
          </button>
        </div>

        <div class="form-group">
          <label class="form-label">Message <span class="text-danger">*</span></label>
          <textarea v-model="form.message" class="form-control" rows="4" placeholder="Bulletin message..." maxlength="500" required></textarea>
          <small class="text-muted">{{ form.message.length }}/500 characters</small>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Type <span class="text-danger">*</span></label>
            <select v-model="form.type" class="form-control" required>
              <option value="">Select type</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="success">Success</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">URL <span class="optional-tag">(Optional)</span></label>
            <input v-model="form.url" type="url" class="form-control" placeholder="https://example.com" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Published At</label>
            <input v-model="form.published_at" type="datetime-local" class="form-control" />
          </div>
          <div class="form-group">
            <label class="form-label">Expires At <span class="optional-tag">(Optional)</span></label>
            <input v-model="form.expires_at" type="datetime-local" class="form-control" />
          </div>
        </div>

        <div v-if="isEditing" class="form-group">
          <label class="form-label">Active</label>
          <select v-model="form.is_active" class="form-control">
            <option :value="true">Active</option>
            <option :value="false">Inactive</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">Language</label>
          <select v-model="form.language" class="form-control" @change="onLanguageChange">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <small v-if="loadingTranslation" class="text-info"><vue-feather type="loader" size="12" class="spin" /> Loading translation...</small>
        </div>
      </form>
    </CreateModal>

    <!-- Bulk Create Modal -->
    <CreateModal
      v-model="showBulkModal"
      title="Bulk Create Bulletin"
      icon="copy"
      size="lg"
      :loading="savingBulk"
      submit-text="Create for Selected Exams"
      :submit-disabled="!bulkForm.title || !bulkForm.message || !bulkForm.type || bulkForm.exam_ids.length === 0"
      @submit="saveBulkBulletins"
    >
      <form @submit.prevent="saveBulkBulletins" class="modal-form">
        <div class="form-group">
          <label class="form-label">Select Exams <span class="text-danger">*</span></label>
          <div class="exam-checkboxes">
            <label class="checkbox-item" @click.prevent="toggleAllExams">
              <input type="checkbox" :checked="bulkForm.exam_ids.length === exams.length" :indeterminate="bulkForm.exam_ids.length > 0 && bulkForm.exam_ids.length < exams.length" />
              <span>Select All ({{ exams.length }})</span>
            </label>
            <div class="exam-checkbox-list">
              <label v-for="exam in exams" :key="exam.id" class="checkbox-item">
                <input type="checkbox" :value="exam.id" v-model="bulkForm.exam_ids" />
                <span>{{ exam.name }}</span>
              </label>
            </div>
          </div>
          <small class="text-muted">{{ bulkForm.exam_ids.length }} exam(s) selected</small>
        </div>

        <div class="form-group">
          <label class="form-label">Title <span class="text-danger">*</span></label>
          <input v-model="bulkForm.title" type="text" class="form-control" placeholder="e.g., Platform Maintenance Notice" maxlength="100" required />
        </div>

        <div class="form-group">
          <label class="form-label">Message <span class="text-danger">*</span></label>
          <textarea v-model="bulkForm.message" class="form-control" rows="4" placeholder="Bulletin message..." maxlength="500" required></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Type <span class="text-danger">*</span></label>
            <select v-model="bulkForm.type" class="form-control" required>
              <option value="">Select type</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="success">Success</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">URL <span class="optional-tag">(Optional)</span></label>
            <input v-model="bulkForm.url" type="url" class="form-control" placeholder="https://example.com" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Published At</label>
            <input v-model="bulkForm.published_at" type="datetime-local" class="form-control" />
          </div>
          <div class="form-group">
            <label class="form-label">Expires At <span class="optional-tag">(Optional)</span></label>
            <input v-model="bulkForm.expires_at" type="datetime-local" class="form-control" />
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Language</label>
          <select v-model="bulkForm.language" class="form-control">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'BulletinsList',
  components: { DataTable, CreateModal },
  setup() {
    const authStore = useAuthStore()
    const { toast, confirmDelete: confirmDeleteAction } = useNotifications()

    const canModify = computed(() => authStore.isSuperAdmin || authStore.isAdmin)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Bulletins', active: true }
    ]

    const columns = [
      { key: 'title', label: 'Title', sortable: true, minWidth: '250px' },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'published', label: 'Published', sortable: true },
      { key: 'expires', label: 'Expires', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    // Data
    const bulletins = ref([])
    const loading = ref(false)
    const totalCount = ref(0)
    const exams = ref([])
    const selectedExam = ref('')
    const loadingExams = ref(false)
    const filterType = ref('')
    const filterActive = ref('')

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const aiEnhancing = ref(false)
    const aiTranslating = ref(false)
    const showTranslateOptions = ref(false)
    const translateTargetLang = ref('hi')
    const loadingTranslation = ref(false)

    const form = reactive({
      title: '',
      message: '',
      type: '',
      url: '',
      published_at: '',
      expires_at: '',
      is_active: true,
      language: 'en'
    })

    // Bulk modal state
    const showBulkModal = ref(false)
    const savingBulk = ref(false)

    const bulkForm = reactive({
      exam_ids: [],
      title: '',
      message: '',
      type: '',
      url: '',
      published_at: '',
      expires_at: '',
      language: 'en'
    })

    // Computed
    const filteredBulletins = computed(() => {
      return bulletins.value.filter(b => {
        const matchesType = !filterType.value || b.type === filterType.value
        const matchesActive = filterActive.value === '' || String(b.is_active) === filterActive.value
        return matchesType && matchesActive
      })
    })

    // Fetch exams
    const fetchExams = async () => {
      loadingExams.value = true
      try {
        const res = await api.get('/v1/admin/exams')
        exams.value = res.data.exams || []
      } catch (err) {
        console.error('Failed to fetch exams:', err)
        toast.error('Failed to load exams')
      } finally {
        loadingExams.value = false
      }
    }

    // Fetch bulletins for selected exam
    const fetchBulletins = async () => {
      if (!selectedExam.value) {
        bulletins.value = []
        totalCount.value = 0
        return
      }
      loading.value = true
      try {
        const res = await api.get('/v1/admin/bulletins', {
          params: { examId: selectedExam.value }
        })
        bulletins.value = res.data.bulletins || []
        totalCount.value = res.data.total || bulletins.value.length
      } catch (err) {
        console.error('Failed to fetch bulletins:', err)
        toast.error('Failed to load bulletins')
        bulletins.value = []
        totalCount.value = 0
      } finally {
        loading.value = false
      }
    }

    const onExamChange = () => {
      filterType.value = ''
      filterActive.value = ''
      fetchBulletins()
    }

    // Form helpers
    const toISOOrNull = (val) => {
      if (!val) return null
      return new Date(val).toISOString()
    }

    const toLocalDatetime = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      const offset = d.getTimezoneOffset()
      const local = new Date(d.getTime() - offset * 60000)
      return local.toISOString().slice(0, 16)
    }

    const resetForm = () => {
      form.title = ''
      form.message = ''
      form.type = ''
      form.url = ''
      form.published_at = ''
      form.expires_at = ''
      form.is_active = true
      form.language = 'en'
      editingId.value = null
      isEditing.value = false
    }

    const resetBulkForm = () => {
      bulkForm.exam_ids = []
      bulkForm.title = ''
      bulkForm.message = ''
      bulkForm.type = ''
      bulkForm.url = ''
      bulkForm.published_at = ''
      bulkForm.expires_at = ''
      bulkForm.language = 'en'
    }

    const openCreateModal = () => {
      if (!selectedExam.value) return
      resetForm()
      showModal.value = true
    }

    const openEditModal = (bulletin) => {
      isEditing.value = true
      editingId.value = bulletin.id
      form.title = bulletin.title || ''
      form.message = bulletin.message || ''
      form.type = bulletin.type || ''
      form.url = bulletin.url || ''
      form.published_at = toLocalDatetime(bulletin.published_at)
      form.expires_at = toLocalDatetime(bulletin.expires_at)
      form.is_active = bulletin.is_active
      form.language = bulletin.language || 'en'
      showModal.value = true
    }

    const openBulkModal = () => {
      resetBulkForm()
      showBulkModal.value = true
    }

    const toggleAllExams = () => {
      if (bulkForm.exam_ids.length === exams.value.length) {
        bulkForm.exam_ids = []
      } else {
        bulkForm.exam_ids = exams.value.map(e => e.id)
      }
    }

    const saveBulletin = async () => {
      if (!form.title || !form.message || !form.type) return
      saving.value = true
      try {
        const payload = {
          title: form.title,
          message: form.message,
          type: form.type,
          url: form.url || null,
          published_at: toISOOrNull(form.published_at) || undefined,
          expires_at: toISOOrNull(form.expires_at),
          language_code: form.language
        }

        if (isEditing.value) {
          payload.is_active = form.is_active
          await api.put(`/v1/admin/bulletins/${editingId.value}`, payload)
          toast.success('Bulletin updated successfully')
        } else {
          payload.exam_id = selectedExam.value
          await api.post('/v1/admin/bulletins', payload)
          toast.success('Bulletin created successfully')
        }
        showModal.value = false
        resetForm()
        fetchBulletins()
      } catch (error) {
        toast.error(error.response?.data?.error?.message || 'Failed to save bulletin')
      } finally {
        saving.value = false
      }
    }

    const saveBulkBulletins = async () => {
      if (!bulkForm.title || !bulkForm.message || !bulkForm.type || bulkForm.exam_ids.length === 0) return
      savingBulk.value = true
      try {
        const payload = {
          exam_ids: bulkForm.exam_ids,
          title: bulkForm.title,
          message: bulkForm.message,
          type: bulkForm.type,
          url: bulkForm.url || null,
          published_at: toISOOrNull(bulkForm.published_at) || undefined,
          expires_at: toISOOrNull(bulkForm.expires_at),
          language: bulkForm.language
        }
        const res = await api.post('/v1/admin/bulletins/bulk', payload)
        toast.success(`Created ${res.data.createdCount || bulkForm.exam_ids.length} bulletin(s)`)
        showBulkModal.value = false
        resetBulkForm()
        fetchBulletins()
      } catch (error) {
        toast.error(error.response?.data?.error?.message || 'Failed to bulk create bulletins')
      } finally {
        savingBulk.value = false
      }
    }

    const toggleBulletin = async (bulletin) => {
      try {
        const res = await api.patch(`/v1/admin/bulletins/${bulletin.id}/toggle`)
        const updated = res.data.bulletin
        const idx = bulletins.value.findIndex(b => b.id === bulletin.id)
        if (idx !== -1 && updated) {
          bulletins.value[idx] = { ...bulletins.value[idx], ...updated }
        } else {
          fetchBulletins()
        }
        toast.success(`Bulletin ${updated?.is_active ? 'activated' : 'deactivated'}`)
      } catch (error) {
        toast.error('Failed to toggle bulletin status')
      }
    }

    const confirmDelete = async (bulletin) => {
      const confirmed = await confirmDeleteAction(bulletin.title)
      if (confirmed) {
        try {
          await api.delete(`/v1/admin/bulletins/${bulletin.id}`)
          bulletins.value = bulletins.value.filter(b => b.id !== bulletin.id)
          totalCount.value = Math.max(0, totalCount.value - 1)
          toast.success('Bulletin deleted')
        } catch (error) {
          toast.error('Failed to delete bulletin')
        }
      }
    }

    // AI Enhancement
    const parseAIBulletinResponse = (response) => {
      let content = ''
      if (response.data?.structured_data?.data) {
        const data = response.data.structured_data.data
        return { message: data.message || '', type: data.type || '' }
      }
      const responseText = response.data?.response || response.data?.message || ''
      const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
      if (jsonMatch) {
        try { return JSON.parse(jsonMatch[1].trim()) } catch (e) {}
      }
      try {
        const parsed = JSON.parse(responseText)
        if (parsed.message) return parsed
      } catch (e) {}
      return { message: responseText }
    }

    const aiEnhanceBulletin = async () => {
      if (!form.title) {
        toast.error('Please enter a title first')
        return
      }
      aiEnhancing.value = true
      try {
        const examName = exams.value.find(e => e.id === selectedExam.value)?.name || 'the exam'
        const response = await api.post('/v1/admin/admin-chat', {
          message: `Write a concise bulletin message for an educational platform.\n\nTitle: "${form.title}"\nExam: "${examName}"\n${form.type ? `Type: ${form.type}` : ''}\n\nProvide your response as a valid JSON object:\n{\n  "message": "Clear, informative bulletin message (max 500 chars)",\n  "type": "info|warning|success|urgent"\n}\n\nKeep the message professional, informative and under 500 characters. Do not wrap in markdown.`,
          generation_type: 'bulletin_enhance',
          output_format: 'structured'
        })
        const aiData = parseAIBulletinResponse(response)
        let fieldsUpdated = 0
        if (aiData.message) { form.message = String(aiData.message).substring(0, 500); fieldsUpdated++ }
        if (aiData.type && ['info', 'warning', 'success', 'urgent'].includes(aiData.type)) {
          if (!form.type) { form.type = aiData.type; fieldsUpdated++ }
        }
        if (fieldsUpdated > 0) toast.success(`AI filled ${fieldsUpdated} field(s)`)
        else toast.warning('AI responded but could not extract structured data.')
      } catch (error) {
        console.error('AI enhancement failed:', error)
        toast.error('Failed to generate bulletin with AI.')
      } finally {
        aiEnhancing.value = false
      }
    }

    // AI Translation
    const aiTranslateBulletin = () => {
      if (!form.title && !form.message) {
        toast.error('Please enter a title or message first')
        return
      }
      showTranslateOptions.value = !showTranslateOptions.value
    }

    const confirmTranslateBulletin = async () => {
      if ((!form.title && !form.message) || !translateTargetLang.value) return
      const targetLangLabel = languageOptions.find(l => l.code === translateTargetLang.value)?.label || translateTargetLang.value
      aiTranslating.value = true
      try {
        const fieldsToTranslate = {}
        if (form.title) fieldsToTranslate.title = form.title
        if (form.message) fieldsToTranslate.message = form.message

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following bulletin content to ${targetLangLabel}. Keep it professional and concise. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })
        const aiData = parseAIBulletinResponse(response)
        let fieldsUpdated = 0
        if (aiData.title) { form.title = aiData.title; fieldsUpdated++ }
        if (aiData.message) { form.message = String(aiData.message).substring(0, 500); fieldsUpdated++ }
        if (fieldsUpdated > 0) {
          form.language = translateTargetLang.value
          toast.success(`${fieldsUpdated} field(s) translated to ${targetLangLabel}`)
        } else {
          toast.warning('AI responded but could not extract translated data.')
        }
        showTranslateOptions.value = false
      } catch (error) {
        console.error('AI translation failed:', error)
        toast.error('Failed to translate bulletin with AI.')
      } finally {
        aiTranslating.value = false
      }
    }

    // Fetch translation when language changes in edit mode
    const onLanguageChange = async () => {
      if (!isEditing.value || !editingId.value) return
      loadingTranslation.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'bulletin', content_id: editingId.value, language_code: form.language }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }
          if (td.title) form.title = td.title
          if (td.message) form.message = td.message
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        loadingTranslation.value = false
      }
    }

    // Helpers
    const getTypeBadge = (type) => {
      const badges = { info: 'bg-info', warning: 'bg-warning text-dark', success: 'bg-success', urgent: 'bg-danger' }
      return badges[type] || 'bg-secondary'
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }

    onMounted(fetchExams)

    return {
      breadcrumbs, columns, bulletins, loading, totalCount,
      exams, selectedExam, loadingExams, filterType, filterActive, filteredBulletins,
      showModal, isEditing, saving, editingId, form, aiEnhancing,
      aiTranslating, showTranslateOptions, translateTargetLang,
      loadingTranslation, onLanguageChange,
      showBulkModal, savingBulk, bulkForm,
      canModify,
      onExamChange, fetchBulletins,
      openCreateModal, openEditModal, openBulkModal, toggleAllExams,
      saveBulletin, saveBulkBulletins, toggleBulletin, confirmDelete,
      aiEnhanceBulletin, aiTranslateBulletin, confirmTranslateBulletin,
      getTypeBadge, formatDate,
      languageOptions
    }
  }
}
</script>

<style scoped>
.filter-cascade-section {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #93c5fd;
}

.filter-cascade-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: #2563eb;
}

.filter-cascade-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.filter-cascade-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.filter-step {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 2px solid #e0e7ff;
  transition: all 0.3s ease;
  opacity: 0.6;
}

.filter-step.active { opacity: 1; border-color: #3b82f6; }
.filter-step.completed { border-color: #3b82f6; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.step-content { flex: 1; display: flex; flex-direction: column; gap: 0.5rem; }

.step-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-select {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-select:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
.filter-select:disabled { background: #f3f4f6; cursor: not-allowed; }

.select-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #f9fafb;
  border-radius: 16px;
  border: 2px dashed #d1d5db;
}

.select-prompt-icon { color: #9ca3af; margin-bottom: 1.5rem; }
.select-prompt h3 { margin: 0 0 0.5rem; font-size: 1.25rem; color: #374151; }
.select-prompt p { margin: 0; color: #6b7280; max-width: 400px; }

.bulletins-content { animation: fadeIn 0.3s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

.modal-form { display: flex; flex-direction: column; gap: 1rem; }
.form-group { display: flex; flex-direction: column; gap: 0.5rem; }
.form-label { font-weight: 500; font-size: 0.875rem; color: #374151; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.optional-tag { font-weight: 400; color: #94a3b8; }

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

.exam-checkboxes { border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden; }

.exam-checkbox-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.15s;
}

.checkbox-item:hover { background: #f3f4f6; }
.checkbox-item:first-child { border-bottom: 1px solid #e5e7eb; background: #f9fafb; font-weight: 500; }

:global(body.dark-mode) .filter-cascade-section { background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); border-color: #334155; }
:global(body.dark-mode) .filter-cascade-header { color: #60a5fa; }
:global(body.dark-mode) .filter-step { background: #1e293b; border-color: #334155; }
:global(body.dark-mode) .filter-step.completed { background: linear-gradient(135deg, #1e293b 0%, #172554 100%); }
:global(body.dark-mode) .filter-select { background: #1e293b; border-color: #374151; color: #f3f4f6; }
:global(body.dark-mode) .select-prompt { background: #111827; border-color: #374151; }
:global(body.dark-mode) .select-prompt h3 { color: #f3f4f6; }
:global(body.dark-mode) .select-prompt p { color: #9ca3af; }
:global(body.dark-mode) .form-label { color: #d1d5db; }
:global(body.dark-mode) .form-control { background: #1e293b; border-color: #374151; color: #f3f4f6; }
:global(body.dark-mode) .exam-checkboxes { border-color: #374151; }
:global(body.dark-mode) .checkbox-item:hover { background: #1e293b; }
:global(body.dark-mode) .checkbox-item:first-child { background: #0f172a; border-color: #334155; }

/* AI Enhance Section */
.ai-enhance-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  border: 1px solid #fbbf24;
  flex-wrap: wrap;
}

.ai-enhance-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-enhance-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.ai-enhance-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-translate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.ai-translate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.ai-hint {
  font-size: 0.8rem;
  color: #92400e;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

:global(body.dark-mode) .ai-enhance-section { background: linear-gradient(135deg, #78350f 0%, #92400e 100%); border-color: #b45309; }
:global(body.dark-mode) .ai-translate-section { background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%); border-color: #0369a1; }
:global(body.dark-mode) .ai-hint { color: #fbbf24; }
</style>
